import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ColumnDef, Row } from '@tanstack/react-table';
import { Dropdown } from '@carbon/react';
import {
  CheckmarkFilled,
  CircleDash,
  InProgress,
  SkipForwardFilled,
  Misuse,
  PauseFilled,
} from '@carbon/react/icons';
import { useAtom } from 'jotai';
import { PageHeader } from 'components/page-header';
import { DataTable } from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import { PlainLink } from 'components/link';
import { StatsBar, type StatItem } from 'components/stats-bar';
import { usePolling } from 'lib/hooks/usePolling';
import {
  hoursAtom,
  stateFilterAtom,
  reasonFilterAtom,
  triggeredByFilterAtom,
  typeCurveAreaFilterAtom,
  sortAtom,
  columnFiltersAtom,
  pageAtom,
  pageSizeAtom,
  inventoryAutomationStatusAtom,
  fetchAutomationStatus,
  buildFingerprint,
} from './atoms';
import { StateCell, WorkspaceCell, OutcomeSummary } from './cells';
import { RunDetail } from './run-detail';
import { RefreshControl } from './refresh-control';
import {
  ALL_OPTION_ID,
  POLLING_INTERVAL_MS,
  LOOKBACK_OPTIONS,
  STATE_OPTIONS,
  STATE_LABELS,
  REASON_OPTIONS,
  REASON_LABELS,
  TRIGGERED_BY_OPTIONS,
  TRIGGERED_BY_LABELS,
  UNSETTLED_STATES,
  unitSuffix,
} from './utils';
import type {
  InventoryAutomationStatus,
  RecomputeState,
  LookbackOption,
  FilterOption,
} from './types';
import styles from './page.module.scss';

const EM_DASH = '—';

// Tag colors for the state strip, chosen to read the same as the state badge in the table.
const STATE_TAG: Record<string, StatItem['tagType']> = {
  pending: 'gray',
  running: 'blue',
  completed: 'green',
  held: 'purple',
  failed: 'red',
};

const STATE_ICON: Record<string, StatItem['renderIcon']> = {
  pending: CircleDash,
  running: InProgress,
  completed: CheckmarkFilled,
  skipped: SkipForwardFilled,
  held: PauseFilled,
  failed: Misuse,
};

// --- Column definitions ---

const baseColumns: ColumnDef<InventoryAutomationStatus, any>[] = [
  {
    accessorKey: 'unitPreview',
    id: 'unitName',
    header: 'Units',
    size: 220,
    meta: { type: 'text' },
    // A run covers many units. The first few are named and the rest are a count, because a run can
    // name twenty-five and a listing cell cannot. Filterable server-side by name (a containment
    // test over the whole array); not sortable, since the names live on another table.
    enableSorting: false,
    cell: (info) => {
      const row = info.row.original;
      if (row.allUnits)
        return <span className={styles.unitsAll}>All units in area</span>;

      const preview = row.unitPreview ?? [];
      const count = row.unitCount ?? 0;
      const remaining = count - preview.length;

      // A single unit is named outright, because that is what most triggers produce and a bare
      // "1 unit" would hide the one fact the reader wants. A batch leads with the count instead,
      // since a truncated list of twenty-five names is noise. The expansion has all of them.
      if (count === 1 && preview[0]) {
        const unit = preview[0];
        return unit.href ? (
          <PlainLink href={unit.href} className={styles.unitLink}>
            {unit.name ?? `#${unit.id}`}
          </PlainLink>
        ) : (
          <>{unit.name ?? `#${unit.id}`}</>
        );
      }

      return (
        <span className={styles.unitsCell}>
          <span className={styles.unitsCount}>{count} units</span>
          {preview.length > 0 && (
            <span className={styles.unitsNames}>
              {preview.map((unit) => unit.name ?? `#${unit.id}`).join(', ')}
              {remaining > 0 && ` +${remaining}`}
            </span>
          )}
        </span>
      );
    },
  },
  {
    accessorKey: 'outcomeCounts',
    id: 'outcomeCounts',
    header: 'Unit Outcomes',
    size: 160,
    // Derived from the run's generation request, so neither server-filterable nor sortable.
    enableColumnFilter: false,
    enableSorting: false,
    cell: (info) => <OutcomeSummary run={info.row.original} />,
  },
  {
    accessorKey: 'uniqueTcId',
    id: 'uniqueTcId',
    header: 'Type Curve Version',
    size: 220,
    // Resolved post-pagination from the type curve version, so neither server-filterable nor
    // server-sortable.
    enableColumnFilter: false,
    enableSorting: false,
    cell: (info) => (
      <span className={styles.mono}>
        {(info.getValue() as string | null) ?? EM_DASH}
      </span>
    ),
  },
  {
    accessorKey: 'formationNames',
    id: 'formationNames',
    header: 'Formations',
    size: 170,
    meta: { type: 'text' },
    // A null list means every formation in the area, which reads as "All" rather than as nothing.
    // Filterable server-side as a containment test, which also matches the area-wide runs: a run
    // covering every formation is one a reader searching for WCA still needs to see.
    enableSorting: false,
    cell: (info) => {
      if (info.row.original.allFormations)
        return <span className={styles.mono}>All formations</span>;
      const names = (info.getValue() as string[] | null) ?? [];
      return names.length ? names.join(', ') : EM_DASH;
    },
  },
  {
    accessorKey: 'typeCurveAreaName',
    id: 'typeCurveAreaName',
    header: 'Type Curve Area',
    size: 170,
    // Filtered via the toolbar "Area" dropdown, not a header filter.
    enableColumnFilter: false,
    enableSorting: false,
    cell: (info) => {
      const name = info.getValue() as string | null;
      return name ?? `#${info.row.original.typeCurveAreaId}`;
    },
  },
  {
    accessorKey: 'typeCurveScenarioName',
    id: 'typeCurveScenarioName',
    header: 'Scenario',
    size: 150,
    enableColumnFilter: false,
    enableSorting: false,
    cell: (info) => {
      const name = info.getValue() as string | null;
      return name ?? `#${info.row.original.typeCurveScenarioId}`;
    },
  },
  {
    accessorKey: 'state',
    id: 'state',
    header: 'State',
    size: 130,
    // Filtered via the toolbar "State" dropdown, not a header filter.
    enableColumnFilter: false,
    cell: (info) => <StateCell state={info.getValue() as RecomputeState} />,
  },
  {
    accessorKey: 'reason',
    id: 'reason',
    header: 'Reason',
    size: 200,
    // Filtered via the toolbar "Reason" dropdown, not a header filter.
    enableColumnFilter: false,
    cell: (info) => {
      const value = info.getValue() as string | null;
      if (!value) return EM_DASH;
      return REASON_LABELS[value] || value;
    },
  },
  {
    accessorKey: 'triggeredBy',
    id: 'triggeredBy',
    header: 'Triggered By',
    size: 180,
    // Filtered via the toolbar "Trigger" dropdown, not a header filter.
    enableColumnFilter: false,
    cell: (info) => {
      const value = info.getValue() as string | null;
      if (!value) return EM_DASH;
      return TRIGGERED_BY_LABELS[value] || value;
    },
  },
  {
    accessorKey: 'message',
    id: 'message',
    header: 'Message',
    size: 320,
    meta: { wrap: true },
    enableColumnFilter: false,
    enableSorting: false,
    cell: (info) => (
      <span className={styles.message}>
        {(info.getValue() as string | null) ?? EM_DASH}
      </span>
    ),
  },
  {
    accessorKey: 'systemWorkspaceName',
    id: 'systemWorkspaceName',
    header: 'System Workspace',
    size: 170,
    enableColumnFilter: false,
    enableSorting: false,
    cell: (info) => (
      <WorkspaceCell
        id={info.row.original.systemWorkspaceId}
        name={info.row.original.systemWorkspaceName}
        href={info.row.original.systemWorkspaceHref}
      />
    ),
  },
  {
    accessorKey: 'createdAt',
    id: 'createdAt',
    header: 'First Seen',
    size: 190,
    meta: { type: 'datetime' },
    // Windowed by the toolbar lookback dropdown, not a header filter.
    enableColumnFilter: false,
  },
  {
    accessorKey: 'updatedAt',
    id: 'updatedAt',
    header: 'Last Recomputed',
    size: 190,
    meta: { type: 'datetime' },
    enableColumnFilter: false,
  },
];

// --- Page component ---

export const InventoryAutomationStatusPage: FC = () => {
  const [hours, setHours] = useAtom(hoursAtom);
  const [stateFilter, setStateFilter] = useAtom(stateFilterAtom);
  const [reasonFilter, setReasonFilter] = useAtom(reasonFilterAtom);
  const [triggeredByFilter, setTriggeredByFilter] = useAtom(
    triggeredByFilterAtom
  );
  const [typeCurveAreaFilter, setTypeCurveAreaFilter] = useAtom(
    typeCurveAreaFilterAtom
  );
  const [sort, setSort] = useAtom(sortAtom);
  const [columnFilters, setColumnFilters] = useAtom(columnFiltersAtom);
  const [page, setPage] = useAtom(pageAtom);
  const [pageSize, setPageSize] = useAtom(pageSizeAtom);
  const [loadableState, refetch] = useAtom(inventoryAutomationStatusAtom);
  const fingerprintRef = useRef<string>('');

  const isLoading = loadableState.state === 'loading';
  const error =
    loadableState.state === 'hasError'
      ? loadableState.error instanceof Error
        ? loadableState.error.message
        : 'Failed to fetch data'
      : null;

  const data =
    loadableState.state === 'hasData'
      ? (loadableState.data?.results ?? [])
      : [];
  const stats =
    loadableState.state === 'hasData' ? loadableState.data?.stats : null;
  const totalCount =
    loadableState.state === 'hasData'
      ? (loadableState.data?.totalCount ?? 0)
      : 0;

  // Keep fingerprint in sync with current data
  useEffect(() => {
    if (loadableState.state === 'hasData' && loadableState.data) {
      fingerprintRef.current = buildFingerprint(loadableState.data);
    }
  }, [loadableState]);

  const hasUnsettledRows = data.some((row) =>
    UNSETTLED_STATES.includes(row.state)
  );

  // Poll in the background while rows can still move. Only trigger a state refetch when the
  // fingerprint actually changes so the table doesn't flicker on unchanged payloads.
  // When the next poll is due. Written by the poll rather than guessed at, because the polling hook
  // schedules the following tick only once this one settles.
  const [nextPollAt, setNextPollAt] = useState<number | null>(null);

  const pollFn = useCallback(async () => {
    const fresh = await fetchAutomationStatus({
      hours,
      stateFilter,
      reasonFilter,
      triggeredByFilter,
      typeCurveAreaFilter,
      sort,
      columnFilters,
      page,
      pageSize,
    });
    const newFingerprint = buildFingerprint(fresh);
    if (newFingerprint !== fingerprintRef.current) {
      refetch();
    }
    setNextPollAt(Date.now() + POLLING_INTERVAL_MS);
  }, [
    hours,
    stateFilter,
    reasonFilter,
    triggeredByFilter,
    typeCurveAreaFilter,
    sort,
    columnFilters,
    page,
    pageSize,
    refetch,
  ]);

  usePolling({
    fn: pollFn,
    intervalMs: POLLING_INTERVAL_MS,
    enabled: hasUnsettledRows,
    immediate: false,
  });

  // The first tick is one interval after polling turns on, and there is nothing due once it turns
  // off. Keyed on the boolean, so this runs when polling starts or stops and not on every refetch.
  useEffect(() => {
    setNextPollAt(hasUnsettledRows ? Date.now() + POLLING_INTERVAL_MS : null);
  }, [hasUnsettledRows]);

  // Deliberately a plain refetch rather than the hook's poll(). poll() is a no-op unless polling is
  // running, which would leave the button dead in the one state a reader most wants it: a settled
  // page they are checking for new work.
  const handleManualRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // The server offers the areas that actually appear inside the lookback window. A picked area that
  // has dropped out of the window is kept in the list, so the dropdown never claims "All Areas"
  // while a filter is still applied.
  const typeCurveAreaOptions = useMemo<FilterOption[]>(() => {
    const areas =
      loadableState.state === 'hasData'
        ? (loadableState.data?.filterOptions?.typeCurveAreas ?? [])
        : [];
    const options = [
      { id: ALL_OPTION_ID, label: 'All Areas' },
      ...areas.map(({ id, label }) => ({ id: String(id), label })),
    ];
    if (
      typeCurveAreaFilter !== ALL_OPTION_ID &&
      !options.some((option) => option.id === typeCurveAreaFilter)
    ) {
      options.push({
        id: typeCurveAreaFilter,
        label: `#${typeCurveAreaFilter}`,
      });
    }
    return options;
  }, [loadableState, typeCurveAreaFilter]);

  // Runs, with the unit count underneath. A run count on its own understates the backlog: one row
  // can be four hundred units, so "4 pending" and "412 units pending" are both true and only the
  // second one tells a reader whether to care.
  const stateStats = useMemo<StatItem[]>(() => {
    if (!stats) return [];
    return [
      { label: 'Total runs', value: stats.total, tagType: 'cool-gray' },
      ...stats.byState.map(({ state, count, unitCount, areaWideRuns }) => ({
        label: STATE_LABELS[state] || state,
        value: count,
        tagType: STATE_TAG[state] ?? 'gray',
        renderIcon: STATE_ICON[state],
        suffix: unitSuffix(unitCount, areaWideRuns),
      })),
    ];
  }, [stats]);

  const reasonStats = useMemo<StatItem[]>(() => {
    if (!stats) return [];
    return stats.byReason.map(({ reason, count }) => ({
      label: REASON_LABELS[reason] || reason,
      value: count,
      tagType: count > 0 ? 'magenta' : 'gray',
    }));
  }, [stats]);

  const handleHoursChange = ({
    selectedItem,
  }: {
    selectedItem: LookbackOption | null;
  }) => {
    if (selectedItem) {
      setHours(parseInt(selectedItem.id, 10));
      setPage(1);
    }
  };

  const handleStateFilterChange = ({
    selectedItem,
  }: {
    selectedItem: FilterOption | null;
  }) => {
    if (selectedItem) {
      setStateFilter(selectedItem.id);
      setPage(1);
    }
  };

  const handleReasonFilterChange = ({
    selectedItem,
  }: {
    selectedItem: FilterOption | null;
  }) => {
    if (selectedItem) {
      setReasonFilter(selectedItem.id);
      setPage(1);
    }
  };

  const handleTriggeredByFilterChange = ({
    selectedItem,
  }: {
    selectedItem: FilterOption | null;
  }) => {
    if (selectedItem) {
      setTriggeredByFilter(selectedItem.id);
      setPage(1);
    }
  };

  const handleTypeCurveAreaFilterChange = ({
    selectedItem,
  }: {
    selectedItem: FilterOption | null;
  }) => {
    if (selectedItem) {
      setTypeCurveAreaFilter(selectedItem.id);
      setPage(1);
    }
  };

  const renderRowSubComponent = useCallback(
    (row: Row<InventoryAutomationStatus>) => <RunDetail run={row.original} />,
    []
  );

  // Expandable only when there is something behind the summary: a run naming units, or one carrying a
  // message. An expander on a row with nothing under it is a promise the panel cannot keep.
  const getRowCanExpand = useCallback(
    (row: Row<InventoryAutomationStatus>) =>
      row.original.allUnits ||
      (row.original.units?.length ?? 0) > 0 ||
      !!row.original.message,
    []
  );

  const handleLoadData = useCallback(
    ({
      page: newPage,
      pageSize: newPageSize,
      sorting,
      columnFilters: newColumnFilters,
    }: TableState) => {
      setPage(newPage);
      setPageSize(newPageSize);
      setSort(
        sorting.length > 0 ? { id: sorting[0].id, desc: sorting[0].desc } : null
      );
      setColumnFilters(
        newColumnFilters.map(({ id, value }) => ({ id, value }))
      );
    },
    [setPage, setPageSize, setSort, setColumnFilters]
  );

  return (
    <div className={styles.page}>
      <PageHeader
        breadcrumbs={[]}
        title="Inventory Automation Status"
        actions={
          <RefreshControl
            autoRefreshing={hasUnsettledRows}
            nextPollAt={nextPollAt}
            refreshing={isLoading}
            onRefresh={handleManualRefresh}
          />
        }
      />
      <p className={styles.subtitle}>
        One row is one rebuild of one type curve area in one scenario. Expand a
        row to see the formations and units it targeted, and what happened to
        each unit.
      </p>

      {stats && (
        <div className={styles.statsGroup}>
          <StatsBar format="value-first" stats={stateStats} />
          <span className={styles.statsLabel}>Deferral reasons</span>
          <StatsBar format="value-first" size="sm" stats={reasonStats} />
        </div>
      )}

      <DataTable
        key={`${hours}-${stateFilter}-${reasonFilter}-${triggeredByFilter}-${typeCurveAreaFilter}`}
        data={data}
        columns={baseColumns}
        getRowId={(row) => String(row.id)}
        isLoading={isLoading}
        error={error}
        persistenceKey="inventory-automation-status"
        initialTableState={{
          sortColumn: sort?.id ?? 'updatedAt',
          sortDirection: sort ? (sort.desc ? 'desc' : 'asc') : 'desc',
          columnFilters,
          pageSize,
        }}
        scroll={{
          mode: 'paginated',
          serverDriven: true,
          totalCount,
        }}
        onPaginationChange={handleLoadData}
        onSortChange={handleLoadData}
        onFilterChange={handleLoadData}
        renderRowSubComponent={renderRowSubComponent}
        getRowCanExpand={getRowCanExpand}
        config={{
          columnResizing: true,
          columnReordering: true,
          columnPinning: true,
          sorting: true,
          columnVisibility: true,
          // The two text searches this page serves are the Unit and Formations header filters, so a
          // toolbar search box would offer a search the server cannot answer.
          globalSearch: false,
          columnFilters: true,
          export: {
            exportPresets: [
              {
                name: 'Export',
                format: 'csv',
                filename: 'inventory-automation-status',
              },
            ],
          },
        }}
        rightToolbarActions={[
          <Dropdown
            key="state"
            id="state-filter-dropdown"
            titleText=""
            label="All States"
            size="sm"
            items={STATE_OPTIONS}
            itemToString={(item: FilterOption | null) => {
              return item ? item.label : '';
            }}
            selectedItem={
              STATE_OPTIONS.find((opt) => opt.id === stateFilter) ??
              STATE_OPTIONS[0]
            }
            onChange={handleStateFilterChange}
            className={styles.toolbarDropdown}
          />,
          <Dropdown
            key="reason"
            id="reason-filter-dropdown"
            titleText=""
            label="All Reasons"
            size="sm"
            items={REASON_OPTIONS}
            itemToString={(item: FilterOption | null) => {
              return item ? item.label : '';
            }}
            selectedItem={
              REASON_OPTIONS.find((opt) => opt.id === reasonFilter) ??
              REASON_OPTIONS[0]
            }
            onChange={handleReasonFilterChange}
            className={styles.toolbarDropdown}
          />,
          <Dropdown
            key="triggered-by"
            id="triggered-by-filter-dropdown"
            titleText=""
            label="All Triggers"
            size="sm"
            items={TRIGGERED_BY_OPTIONS}
            itemToString={(item: FilterOption | null) => {
              return item ? item.label : '';
            }}
            selectedItem={
              TRIGGERED_BY_OPTIONS.find(
                (opt) => opt.id === triggeredByFilter
              ) ?? TRIGGERED_BY_OPTIONS[0]
            }
            onChange={handleTriggeredByFilterChange}
            className={styles.toolbarDropdown}
          />,
          <Dropdown
            key="type-curve-area"
            id="type-curve-area-filter-dropdown"
            titleText=""
            label="All Areas"
            size="sm"
            items={typeCurveAreaOptions}
            itemToString={(item: FilterOption | null) => {
              return item ? item.label : '';
            }}
            selectedItem={
              typeCurveAreaOptions.find(
                (opt) => opt.id === typeCurveAreaFilter
              ) ?? typeCurveAreaOptions[0]
            }
            onChange={handleTypeCurveAreaFilterChange}
            className={styles.toolbarDropdown}
          />,
          <Dropdown
            key="hours"
            id="lookback-dropdown"
            titleText=""
            label="Last 24 hours"
            size="sm"
            items={LOOKBACK_OPTIONS}
            itemToString={(item: LookbackOption | null) => {
              return item ? item.label : '';
            }}
            selectedItem={LOOKBACK_OPTIONS.find(
              (opt) => opt.id === String(hours)
            )}
            onChange={handleHoursChange}
            className={styles.toolbarDropdown}
          />,
        ]}
      />
    </div>
  );
};
